import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Grid,
    IconButton,
    Menu,
    TablePagination,
    TableSortLabel,
    TextField,
    Typography
} from "@mui/material";
import React, {useEffect} from "react";
import {Clear} from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import {Link} from "react-router-dom";
import {TABLE_ROW_LENGTH_OPTIONS} from "../../../config/table";
import {useDispatch, useSelector} from "react-redux";
import {FilterItemChip} from "../../drafts/types";
import {useDeleteCreationMutation, useLazyGetCreationDetailsQuery} from "../../creations/api/creationsApiSlice";
import {useTranslation} from "react-i18next";
import useDebounce from "../../../hooks/useDebounce";
import {CreationDetails} from "../../creations/types";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {mediaFormatDimensions} from "../../../config/mediaFormatDimensions";
import {
    useDownloadEmissionFileUrlMutation,
    useGetEmissionsFiltersMetaQuery,
    useGetEmissionsQuery,
    useResumeEmissionMutation,
    useStopEmissionMutation
} from "../api/emissionsApiSlice";
import CmEmissionsStatus from "../../../components/CmEmissionsStatus/CmEmissionsStatusIconBox";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import {initialEmissionsState, selectCurrentFiltersState, setFilters} from "../store/emissionsSlice";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {useSnackbar} from "notistack";
import ConfirmDialog from "../../shared/confirmDialog/ConfirmDialog";
import {EmissionsFilters} from "../types";
import {useGetCmEnumsQuery} from "../../../app/appApiSlice";
import {visuallyHidden} from "@mui/utils";
import {BRAIN_EMISSION_ID_SIGNATURE, Order} from "../../../config/general";
import ability from "../../../utils/ability";

export enum EmissionAction {
    Stop = 'Stop',
    Resume = 'Resume',
}

const EmissionsList = () => {
    const [searchByName, setSearchByName] = React.useState<string>('');
    const currentFiltersState = useSelector(selectCurrentFiltersState);
    const [rowLength, setRowLength] = React.useState(TABLE_ROW_LENGTH_OPTIONS[0]);
    const [skipFetchingCreations, setSkipFetchingCreations] = React.useState(false);
    const [getCreationDetails, {data: creationDetailsData}] = useLazyGetCreationDetailsQuery();
    const [openActionMenu, setOpenActionMenu] = React.useState<number | null>(null);
    const [anchorElForActionMenu, setAnchorElForActionMenu] = React.useState<null | HTMLElement>(null);
    const [openStopResumeEmission, setOpenStopResumeEmission] = React.useState<{
        id: number;
        action: EmissionAction;
    } | null>(null);
    const [orderBy, setOrderBy] = React.useState<string>('name');
    const [order, setOrder] = React.useState<Order>('asc');
    const [deleteCreation, {isLoading: deletingCreation}] = useDeleteCreationMutation()
    const [stopEmission, {isLoading: loadingStopEmission}] = useStopEmissionMutation()
    const [resumeEmission, {isLoading: loadingResumeEmission}] = useResumeEmissionMutation()
    const [downloadEmissionFileUrl, {isLoading: loadingDownloadEmissionFileUrl}] = useDownloadEmissionFileUrlMutation()

    const {enqueueSnackbar} = useSnackbar();


    let params = [`items=${rowLength.toString()}`];
    let filterItemChips: FilterItemChip[] = []
    for (const queryKey in currentFiltersState) {
        const value = currentFiltersState[queryKey as keyof typeof currentFiltersState]
        console.log(value)
        console.log(queryKey)
        if (Array.isArray(value)) {
            value.forEach(item => {
                params.push(`q[${queryKey}][]=${item.toString()}`)
                filterItemChips.push({name: queryKey, value: item, isArray: true})
            })
        } else if (queryKey === 'page') {
            params.push(`page=${(value || 0).toString()}`)
        } else if (queryKey === 'draftIdIn' && value) {
            params.push(`q[${queryKey}][]=${value.toString()}`)
            filterItemChips.push({name: queryKey, value: value.toString(), isArray: false})
        } else if (value) {
            params.push(`q[${queryKey}]=${value.toString()}`)
            if (queryKey !== 's') {
                filterItemChips.push({name: queryKey, value: value.toString(), isArray: false})
            }
        }
    }


    const {
        isLoading: loadingFiltersMeta,
        isError: isFiltersMetaError,
        isSuccess: isSuccessFiltersMeta,
        data: filtersMetaData
    } =
        useGetEmissionsFiltersMetaQuery();

    const {isLoading: loadingCmEnums, data: cmEnums} =
        useGetCmEnumsQuery();


    const {isLoading, isError, isSuccess, data} =
        useGetEmissionsQuery(params.join('&').toString(), {skip: skipFetchingCreations});


    const dispatch = useDispatch();
    const {t} = useTranslation(); // not passing any namespace will use the defaultNS (by default set to 'translation')

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;


    const emissions = data?.data


    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        dispatch(setFilters({
            ...currentFiltersState,
            s: `${property} ${isAsc ? 'desc' : 'asc'}`,
            page: 1
        }))
    };

    const createSortHandler =
        (property: string) => (event: React.MouseEvent<unknown>) => {
            handleRequestSort(event, property);
        };


    useEffect(() => {
        return () => {
            dispatch(setFilters({
                ...initialEmissionsState.filters
            }))
        }
    }, []);

    useDebounce(() => {
            dispatch(setFilters({
                ...currentFiltersState,
                nameCont: searchByName,
                page: 1
            }))
        }, [searchByName], 800
    );

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setRowLength(parseInt(event.target.value, 10))
    }

    function removeFilter(filterElement: FilterItemChip) {
        if (filterElement.isArray) {
            dispatch(setFilters({
                ...currentFiltersState,
                page: 1,
                [filterElement.name]: (currentFiltersState[filterElement.name as keyof EmissionsFilters] as string[]).filter(el => el !== filterElement.value)
            }))
        } else {
            if (filterElement.name === 'nameCont') {
                setSearchByName('');
            }
            dispatch(setFilters({
                ...currentFiltersState,
                page: 1,
                [filterElement.name]: ''
            }))
        }
    }

    function handleOpenCreationPreview(creationId: number | null) {
        if (!creationId) {
            return;
        }
        let creationDetailsData: CreationDetails;
        getCreationDetails(creationId.toString()).unwrap().then((creationDetails: CreationDetails) => {
            creationDetailsData = creationDetails;
        })
            .catch((error: any) => {
                if (error && error.data && error.data.status === 404) {
                    enqueueSnackbar('Wystąpił błąd, kreacja nie istnieje', {variant: 'error'});
                }
            })
            .finally(() => {
                if (!creationDetailsData) {
                    return
                }

                const fileUrl = encodeURIComponent(creationDetailsData.creationFileUrl);
                const playerPreviewPropsLink = encodeURIComponent(creationDetailsData.playerPreviewPropsLink)
                const [w, h] = mediaFormatDimensions[creationDetailsData.mediaFormat as keyof typeof mediaFormatDimensions];
                const toolbarHeight = 56; // preview buttons row
                const windowWidth = w;
                const windowHeight = h + toolbarHeight;

                window.open(
                    `/creations/${creationId}/preview?datajson=${playerPreviewPropsLink}&datapath=${process.env.REACT_APP_DSP_BACKEND_API}&fileUrl=${fileUrl}&mediaFormat=${creationDetailsData.mediaFormat}`,
                    "_blank",
                    [
                        `width=${windowWidth}`,
                        `height=${windowHeight}`,
                        'location=no',
                        'status=no',
                        'scrollbars=no',
                        'toolbar=no',
                        'menubar=no',
                        'resizable=yes'
                    ].join(',')
                );
            })
    }

    return (<Box className="CreationsList">
        <Box className={'page-header'}>
            <h1>Podgląd emisji</h1>
        </Box>

        {(isLoading || loadingFiltersMeta) && (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                <CircularProgress size={100}/>
            </Box>)}

        {/*{isError && (*/}
        {/*    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>*/}
        {/*        <span>Error occurred...</span>*/}
        {/*    </Box>)}*/}

        {(emissions && filtersMetaData) && (<>
            <Grid container spacing={2}>
                <Grid item xs={4} alignItems="baseline">
                    <TextField
                        size="small"
                        value={searchByName}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchByName(event.target.value);
                        }}
                        InputProps={{
                            endAdornment: <IconButton
                                onClick={() => {
                                    setSearchByName('')
                                }}
                                edge="end"
                            >
                                {<Clear/>}
                            </IconButton>,
                        }}
                        margin="normal"
                        variant={"outlined"}
                        fullWidth
                        id="creationsName"
                        label="Nazwa kreacji"
                        name="creationsName"
                        autoFocus></TextField>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth sx={{marginTop: '15px'}}>
                        <InputLabel shrink id="demo-simple-select-label">Status emisji</InputLabel>
                        <Select
                            size="small"
                            variant={"outlined"}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Status emisji"
                            value={currentFiltersState.emissionStatusEq || ''}
                            notched
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    emissionStatusEq: event.target.value,
                                    page: 1
                                }))
                            }}
                        >
                            {filtersMetaData.data.filtering.fields.emissionStatusEq.allowedValues.map((key, index) => (
                                <MenuItem
                                    key={key + index}
                                    value={key}>{t(`emissions.status.${key}`)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        maxDate={dayjs(new Date(currentFiltersState.endAtGteq))}
                        onChange={(event) => {
                            dispatch(setFilters({
                                ...currentFiltersState,
                                startAtLteq: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : '',
                                page: 1
                            }))
                        }}
                        value={currentFiltersState.startAtLteq ? dayjs(currentFiltersState.startAtLteq) : null}
                        sx={{marginTop: '14px', width: '100%'}} slotProps={{textField: {size: 'small'}}} label="Od"/>
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        minDate={currentFiltersState.startAtLteq ? dayjs(currentFiltersState.startAtLteq) : undefined}
                        onChange={(event) => {
                            dispatch(setFilters({
                                ...currentFiltersState,
                                endAtGteq: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : '',
                                page: 1
                            }))
                        }}
                        sx={{marginTop: '14px', width: '100%'}}
                        value={currentFiltersState.endAtGteq ? dayjs(currentFiltersState.endAtGteq) : null}
                        slotProps={{textField: {size: 'small'}}} label="Do"/>
                </Grid>
                <Grid item xs={4} sx={{marginBottom: '15px'}}>
                    <FormControl fullWidth>
                        {<Autocomplete
                            multiple
                            size={'small'}
                            id="tags-filled"
                            options={filtersMetaData.data.filtering.fields.tagsNameIn.allowedValues.map((option) => option)}
                            value={currentFiltersState.tagsNameIn}
                            onChange={(_, value) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    tagsNameIn: value,
                                    page: 1
                                }))
                            }}
                            renderTags={(value: readonly string[], getTagProps) =>
                                value.map((option: string, index: number) => {
                                    const {key, ...tagProps} = getTagProps({index});
                                    if (index === 0) {
                                        return (
                                            <Typography sx={{marginLeft: '10px'}}>
                                                {option}
                                            </Typography>
                                        )
                                    }

                                    return (
                                        <Typography>
                                            {`, ${option}`}
                                        </Typography>
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Tagi"
                                />
                            )}
                        />}
                    </FormControl>
                </Grid>
                <Grid item xs={4} sx={{marginBottom: '15px'}}>

                    {cmEnums && (<FormControl fullWidth>
                        <Autocomplete
                            multiple
                            size={'small'}
                            id="tags-filled"
                            options={(cmEnums.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                id: number;
                                name: string
                            }[]).map(el => el.id)}
                            value={currentFiltersState.adTagsIdIn}
                            noOptionsText={'Brak ad tagów...'}
                            renderOption={({key, ...props}, option: number) => (
                                <li key={key} {...props}>{(cmEnums.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                    id: number;
                                    name: string
                                }[]).filter(el => el.id === option)[0].name}
                                </li>
                            )}
                            onChange={(event, value) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    adTagsIdIn: value,
                                    page: 1
                                }));
                            }}
                            filterOptions={(options, {inputValue}) => {
                                return (cmEnums.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                    id: number;
                                    name: string
                                }[]).filter(item => item.name.toLowerCase().includes(inputValue.toLowerCase())).map(el => el.id);
                            }}
                            renderTags={(selected) => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {(selected as any).map((value: any, index: number) => {

                                        let element = (cmEnums?.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                            id: number;
                                            name: string
                                        }[]).filter(el => el.id === value)[0].name

                                        if (index === 0) {
                                            return (
                                                <Typography sx={{marginLeft: '10px'}}>
                                                    {element}
                                                </Typography>
                                            )
                                        }

                                        return (
                                            <Typography>
                                                {`, ${element}`}
                                            </Typography>
                                        );
                                    })}
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Ad tagi"
                                />
                            )}
                        />
                    </FormControl>)}
                </Grid>
                {!ability.can('access', 'SimplifiedInterface') && (<Grid item xs={2}>
                    <FormControl fullWidth sx={{marginBottom: '15px'}}>
                        <InputLabel shrink id="demo-simple-select-label">Format nośnika</InputLabel>
                        <Select
                            size="small"
                            variant={"outlined"}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Format nośnika"
                            multiple
                            value={currentFiltersState.draftMediaFormatIn || ''}
                            notched
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    draftMediaFormatIn: typeof event.target.value === "string" ? [event.target.value] : event.target.value,
                                    page: 1
                                }))
                            }}
                        >
                            {filtersMetaData && filtersMetaData.data.filtering.fields.draftMediaFormatIn.allowedValues.map((mediaFormat) => (
                                <MenuItem
                                    key={mediaFormat}
                                    value={mediaFormat}>{mediaFormat}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>)}
                { !ability.can('access', 'SimplifiedInterface') && (<Grid item xs={4}>
                    {filtersMetaData && (<Box sx={{marginBottom: '15px'}}><Autocomplete
                        fullWidth={true}
                        disablePortal
                        onChange={(_, value) => {
                            dispatch(setFilters({
                                ...currentFiltersState,
                                draftIdIn: value ? value[1] : null,
                                page: 1
                            }))
                        }}
                        value={currentFiltersState.draftIdIn ? [filtersMetaData.data.filtering.fields.draftIdIn.allowedValues.filter(el => el[1] === currentFiltersState.draftIdIn)[0][0], currentFiltersState.draftIdIn] as [string, number] : null}
                        size="small"
                        options={filtersMetaData.data.filtering.fields.draftIdIn.allowedValues}
                        getOptionLabel={(draftsAllowedValues) => {
                            return `${draftsAllowedValues[0]}`;
                        }}
                        renderOption={(props, option, state, ownerState) => {
                            const {key, id, ...optionProps} = props;

                            return (<Box
                                key={id}
                                component="li"
                                {...optionProps}
                            >
                                {option[0]}
                            </Box>)
                        }}
                        renderInput={(params) => <TextField {...params} label="Schemat"/>}
                    /></Box>)}
                </Grid>)}

            </Grid>

            <Grid container spacing={2} pb={2}>
                {filterItemChips.map((filter) => {

                    let chipValue = filter.value

                    if (filter.name === 'emissionStatusEq') {
                        chipValue = t(`emissions.status.${filter.value}`)
                    } else if (filter.name === 'draftIdIn') {
                        chipValue = filtersMetaData.data.filtering.fields.draftIdIn.allowedValues.filter(el => el[1] === parseInt(filter.value as string))[0][0]
                    } else if (filter.name === 'adTagsIdIn') {
                        chipValue = (cmEnums?.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                            id: number;
                            name: string
                        }[]).filter(el => el.id === filter.value)[0].name
                    }


                    return (
                        <Grid item key={filter.value}>
                            <Chip
                                color="info"
                                label={(
                                    <React.Fragment>{t(`filters.keys.${filter.name}`) + ': '}<strong>{chipValue}</strong></React.Fragment>)}
                                onClick={() => {
                                }}
                                onDelete={() => removeFilter(filter)}
                            />
                        </Grid>)
                })}
            </Grid>
            <Backdrop
                sx={(theme) => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
                open={deletingCreation || isLoading}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>

            {emissions && (<TableContainer component={Paper}>
                <Table sx={{minWidth: 650}} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left"></TableCell>
                            <TableCell align="left">Id</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={createSortHandler('name')}
                                >
                                    Nazwa kreacji
                                    {orderBy === 'name' ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Status</TableCell>
                            <TableCell align="right">Tagi</TableCell>
                            <TableCell align="right">Ad tagi</TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={orderBy === 'start_at'}
                                    direction={orderBy === 'start_at' ? order : 'asc'}
                                    onClick={createSortHandler('start_at')}
                                >
                                    Data rozpoczecia
                                    {orderBy === 'start_at' ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={orderBy === 'end_at'}
                                    direction={orderBy === 'end_at' ? order : 'asc'}
                                    onClick={createSortHandler('end_at')}
                                >
                                    Data zakończenia
                                    {orderBy === 'end_at' ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {emissions.map((row) => (
                            <>
                                <TableRow
                                    key={row.id}
                                    sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                    <TableCell component="th" scope="row">
                                        {row.thumbnailUrl ? <Box
                                            component="img"
                                            sx={{
                                                height: 80,
                                                width: 120,
                                                maxHeight: {xs: 233, md: 167},
                                                maxWidth: {xs: 350, md: 250},
                                            }}
                                            alt="thumbnail"
                                            src={row.thumbnailUrl}
                                        /> : ''}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {row.id && (BRAIN_EMISSION_ID_SIGNATURE + row.id)}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {row.name}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
                                            <CmEmissionsStatus emissionStatus={row.emissionStatus}></CmEmissionsStatus>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{row.tagList.join(', ')}</TableCell>
                                    <TableCell align="right">{row.adTags.map(tag => tag.name).join(', ')}</TableCell>
                                    <TableCell align="right">
                                        {new Date(row.startAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        {new Date(row.endAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <ButtonGroup variant="text" aria-label="Basic button group">
                                            <Link to={`/creations/${row.fromCreationId}`} className="link"><Button>Edycja
                                                kreacji</Button></Link>
                                            <Button onClick={() => {
                                                handleOpenCreationPreview(row.id);
                                            }}>Podgląd kreacji</Button>
                                            <IconButton
                                                sx={{width: 60}}
                                                aria-label="fingerprint" color="primary"
                                                id="long-button"
                                                onClick={(actionEvent: React.MouseEvent<HTMLElement>) => {
                                                    setOpenActionMenu(row.id)
                                                    setAnchorElForActionMenu(actionEvent.currentTarget);
                                                }}
                                                aria-controls={openActionMenu ? 'long-menu' : undefined}
                                                aria-expanded={openActionMenu ? 'true' : undefined}
                                                aria-haspopup="true">
                                                <MoreVertIcon className="action-menu"/>
                                            </IconButton>
                                            <Menu
                                                id="long-menu"
                                                MenuListProps={{
                                                    'aria-labelledby': 'long-button',
                                                }}
                                                anchorEl={anchorElForActionMenu}
                                                open={openActionMenu === row.id}
                                                onClose={() => {
                                                    setAnchorElForActionMenu(null);
                                                    setOpenActionMenu(null)
                                                }}
                                                slotProps={{
                                                    paper: {
                                                        style: {
                                                            maxHeight: ITEM_HEIGHT * 4.5,
                                                            width: '20ch',
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem
                                                    onClick={async (event) => {
                                                        if (loadingDownloadEmissionFileUrl) {
                                                            return
                                                        }
                                                        try {
                                                            const downloadUrl = await downloadEmissionFileUrl(row?.id).unwrap()
                                                            const image = await fetch(downloadUrl)
                                                            const imageBlog = await image.blob()
                                                            const imageURL = URL.createObjectURL(imageBlog)

                                                            const link = document.createElement('a')
                                                            link.href = imageURL
                                                            link.download = row.name
                                                            document.body.appendChild(link)
                                                            link.click()
                                                            document.body.removeChild(link)
                                                        } catch (e) {
                                                            console.log(e)
                                                        }


                                                    }}>
                                                    <span>Pobierz emisje </span> {loadingDownloadEmissionFileUrl && (
                                                    <CircularProgress
                                                        sx={{marginLeft: '8px'}} size={20} color="inherit"/>)}
                                                </MenuItem>
                                                <MenuItem
                                                    disabled={row.emissionStatus === 'paused' || row.emissionStatus === 'stopped' || row.emissionStatus === 'archived'}
                                                    onClick={(event) => {
                                                        setOpenStopResumeEmission({
                                                            id: row.id!,
                                                            action: EmissionAction.Stop
                                                        })
                                                    }}>
                                                    Zatrzymaj emisje
                                                </MenuItem>
                                                <MenuItem
                                                    disabled={row.emissionStatus === 'active' || row.emissionStatus === 'archived'}
                                                    onClick={(event) => {
                                                        setOpenStopResumeEmission({
                                                            id: row.id!,
                                                            action: EmissionAction.Resume
                                                        })
                                                    }}>
                                                    Wznów emisje
                                                </MenuItem>

                                            </Menu>
                                        </ButtonGroup>
                                    </TableCell>
                                </TableRow>

                                {openStopResumeEmission?.id === row.id && (<ConfirmDialog
                                    title={`${openStopResumeEmission.action === EmissionAction.Stop ? 'Zatrzymanie' : 'Wznowienie'} emisji`}
                                    description={`Czy na pewno chcesz ${openStopResumeEmission.action === EmissionAction.Stop ? 'zatrzymać' : 'wznowić'} emisje ${row.name}?`}
                                    confirmText={`${openStopResumeEmission.action === EmissionAction.Stop ? 'Zatryzmaj' : 'Wznów'}`}
                                    open={openStopResumeEmission?.id === row.id}
                                    onConfirm={async () => {
                                        try {
                                            if (row.id) {
                                                await openStopResumeEmission.action === EmissionAction.Stop ? stopEmission(row?.id).unwrap() : resumeEmission(row?.id).unwrap()
                                                enqueueSnackbar(`Pomyślnie ${openStopResumeEmission.action === EmissionAction.Stop ? 'zatrzymano' : 'wznowiono'}  emisje`, {variant: 'success'});
                                                setOpenStopResumeEmission(null)
                                                setAnchorElForActionMenu(null);
                                                setOpenActionMenu(null)
                                            }

                                        } catch (err: any) {
                                            enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                                            setOpenStopResumeEmission(null)
                                            setAnchorElForActionMenu(null);
                                            setOpenActionMenu(null)
                                        }
                                    }}
                                    onCancel={() => {
                                        setOpenStopResumeEmission(null)
                                        setAnchorElForActionMenu(null);
                                        setOpenActionMenu(null)
                                    }}></ConfirmDialog>)}
                            </>

                        ))}
                    </TableBody>
                </Table>
            </TableContainer>)}

            <TablePagination
                rowsPerPageOptions={TABLE_ROW_LENGTH_OPTIONS}
                component="div"
                count={data.meta.count}
                rowsPerPage={rowLength}
                page={data.meta.page - 1}
                onPageChange={(e, page) => dispatch(setFilters({
                    ...currentFiltersState,
                    page: page + 1
                }))}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>)}
    </Box>)
};

export default EmissionsList;
